/**
 * size-algorithm.js — YOUR SIZING ALGORITHM PLUGS IN HERE
 * =========================================================
 * The flow UI (custom-size-flow.js) calls:
 *
 *   window.CustomSizing.computeSize(inputs) -> Promise<result>
 *
 * INPUT (metric unless noted; the UI normalizes units):
 *   {
 *     heightCm:   number,            // e.g. 182
 *     weightKg:   number,            // e.g. 78
 *     age:        number,            // optional; no longer asked, see below
 *     shoeSizeEu: number,            // e.g. 43
 *     chestIdx:   number,            // 0-5 index into the chest choices
 *     chestRange: string,            // e.g. '40-42' (inches)
 *     chestAlpha: string,            // e.g. 'M'
 *     collarIn:   number,            // e.g. 16 (inches)
 *     waistIn:    number,            // e.g. 34 (inches)
 *     fit:        'slim' | 'regular' | 'relaxed'
 *   }
 *
 * REQUIRED OUTPUT:
 *   {
 *     recommendedSize:  string,      // must match a product Size option value
 *     recommendedBuild: string,      // must match a product Build option value
 *                                    // (only read when the product has one)
 *     measurements: {                // garment measurements in cm
 *       chestCm:         number,
 *       shoulderWidthCm: number,
 *       collarCm:        number,
 *       waistCm:         number,
 *       sleeveLengthCm:  number
 *     }
 *   }
 *
 * TO REPLACE THE PLACEHOLDER:
 *  - Client-side formula: rewrite the body of computeSize below.
 *  - Server-side: fetch your endpoint instead, e.g. a Shopify app proxy:
 *
 *      const res = await fetch('/apps/sizing/compute', {
 *        method: 'POST',
 *        headers: { 'Content-Type': 'application/json' },
 *        body: JSON.stringify(inputs)
 *      });
 *      return await res.json();
 *
 * The UI awaits the promise and shows a loading state. Throw an Error to
 * surface a friendly retry message.
 */

window.CustomSizing = window.CustomSizing || {};

/* cfg is the assigned Size flow's sizing settings, passed by
 * custom-size-flow.js: { sizeChart, easeSlim, easeRegular, easeRelaxed,
 * sizeLadder }. Every field is optional and falls back to the values below,
 * so calling computeSize(inputs) alone still works. */
window.CustomSizing.computeSize = async function (inputs, cfg) {
  cfg = cfg || {};
  /* ------------------------------------------------------------------
   * Uses the customer's stated chest band where given, narrowed by a chest
   * estimated from height, weight and age; with no stated chest, the
   * estimate alone. Waist can raise the size; see below.
   *
   * The estimator is a least-squares fit on ANSUR II (2012 US Army
   * anthropometric survey, male working database, n=4082, cleared for
   * unlimited public release). It takes height and weight, plus collar and
   * waist where the flow asks for them. Held-out performance on a 20% split,
   * with collar and waist degraded to what a customer actually tells us
   * (half-inch collar sizes, whole-inch trouser sizes):
   *
   *   height + weight                    MAE 2.63 cm, 90% within 5.5 cm
   *   height + weight + collar + waist   MAE 2.55 cm, 90% within 5.3 cm
   *
   * So collar and waist are in, and they do move an individual's estimate
   * (about 0.75 cm per inch of collar, 0.36 cm per inch of waist), but they
   * add little once height and weight are known. Waist earns its place in
   * the size separately, further down.
   *
   * The height term is negative on purpose: at a FIXED weight a taller man
   * is leaner and measures smaller round the chest.
   *
   * Shoe size is deliberately not an input. On ANSUR II it moved the
   * estimate by 0.016 cm once height, weight and age were known. The flow
   * still asks for it and still records it on the order, so the question
   * can be re-evaluated against real Barnfield fit data later.
   *
   * CAVEAT: ANSUR II is US military personnel, median age 28. The
   * relationship between height, weight and chest travels reasonably; the
   * population is not a match for a UK menswear customer. And this predicts
   * BODY chest, not garment size - ease comes from the flow's own settings.
   * ------------------------------------------------------------------ */
  const { chestRange, chestAlpha, fit } = inputs;
  const heightCm = toNum(inputs.heightCm);
  const weightKg = toNum(inputs.weightKg);
  const waistIn = toNum(inputs.waistIn);
  const collarIn = toNum(inputs.collarIn);

  /* The flow no longer asks for age, so the fits below leave it out. Where a
   * flow does supply one it is applied as a correction about the survey's
   * mean age: chest grows about 1 cm a decade at the same height and weight. */
  const SURVEY_MEAN_AGE = 30.2;
  const age = Number.isFinite(Number(inputs.age)) && Number(inputs.age) > 0
    ? Number(inputs.age)
    : SURVEY_MEAN_AGE;

  /* Body chest circumference.
   *
   * A stated chest is a 2" band, which is as wide as one whole size, so
   * taking its midpoint throws away where in the band the customer sits -
   * and that is exactly what decides between two neighbouring sizes. Height
   * and weight say where: the ANSUR II fit above gives an independent chest
   * estimate, and the stated band is then narrowed towards it.
   *
   * The band stays the authority. The answer is the mean of the estimate's
   * error distribution cut off at the band's edges, so it never leaves the
   * band the customer picked: a lean 38-40" man lands near 38", a heavy one
   * near 40", and when the estimate agrees with the midpoint nothing moves.
   * ESTIMATE_SD_CM is the fit's held-out error as a standard deviation.
   *
   * With no stated chest the estimate is used as it stands; with no height
   * or weight the midpoint is. */
  const ESTIMATE_SD_CM = 3.3;
  const hasBuild = isNum(heightCm) && isNum(weightKg);
  let buildChestCm = null;
  if (hasBuild) {
    // [intercept, height cm, weight kg, collar in, trouser waist in]
    const fitCoef = isNum(collarIn) && isNum(waistIn) ? [78.6341, -0.2180, 0.4675, 0.7489, 0.3591]
      : isNum(collarIn) ? [92.9264, -0.2799, 0.5815, 0.7235, 0]
      : isNum(waistIn) ? [89.9665, -0.2305, 0.5130, 0, 0.3529]
      : [103.6434, -0.2909, 0.6235, 0, 0];
    buildChestCm = fitCoef[0]
      + fitCoef[1] * heightCm
      + fitCoef[2] * weightKg
      + fitCoef[3] * (collarIn || 0)
      + fitCoef[4] * (waistIn || 0)
      + 0.1 * (age - SURVEY_MEAN_AGE);
  }

  let chestCircCm;
  let statedChestCm = null;
  let chestConflict = false;
  const band = parseBand(chestRange);
  if (band) {
    const loCm = band[0] * 2.54;
    const hiCm = band[1] * 2.54;
    statedChestCm = (loCm + hiCm) / 2;
    chestCircCm = buildChestCm != null
      ? truncatedMean(buildChestCm, ESTIMATE_SD_CM, loCm, hiCm)
      : statedChestCm;
    /* Height and weight put this customer a full band or more away from the
     * chest they stated. The stated chest still wins - people know their own
     * jacket size better than a regression does - but it is recorded, so the
     * studio can see which orders are worth a second look. */
    chestConflict = buildChestCm != null &&
      (buildChestCm < loCm - (hiCm - loCm) || buildChestCm > hiCm + (hiCm - loCm));
  } else if (buildChestCm != null) {
    chestCircCm = buildChestCm;
  } else {
    throw new Error('Not enough answers to estimate a chest measurement');
  }

  /* Waist.
   *
   * A coat has to close at the waist as well as the chest. For most men the
   * chest is the larger of the two by a wide margin and decides the size on
   * its own; for a man who carries his weight in front it is the waist that
   * decides, and sizing him on chest alone hands him a coat that fastens at
   * the top button and not the bottom one.
   *
   * So the size is matched against whichever is the more demanding: the
   * chest, or the waist plus the amount the pattern narrows from chest to
   * waist (a coat 3 cm smaller at the waist than the chest has to be chosen
   * 3 cm bigger in the chest to give the waist the same room). In practice
   * the waist takes over when a man's trouser size is within about 3" of
   * his chest size - roughly one man in twenty on ANSUR II.
   *
   *   TROUSER_TO_WAIST_CM  a trouser size understates the body, because
   *                        trousers sit below the natural waist and are
   *                        labelled small. 4 cm is an ESTIMATE, checked
   *                        against one person (32" trousers, 85 cm waist).
   *   WAIST_SUPPRESSION_CM how much smaller the finished coat is round the
   *                        waist than round the chest. From the Hampden in
   *                        S: 50 cm flat at the waist against 51.5 cm at the
   *                        chest, so 3 cm round. Assumed the same in every
   *                        size.
   *
   * Only applies when the flow asks for a waist; knitwear does not. */
  const TROUSER_TO_WAIST_CM = 4;
  const WAIST_SUPPRESSION_CM = num(cfg.waistSuppressionCm, 3);
  const bodyWaistCm = isNum(waistIn) ? waistIn * 2.54 + TROUSER_TO_WAIST_CM : null;
  const waistGirthCm = bodyWaistCm != null ? bodyWaistCm + WAIST_SUPPRESSION_CM : null;
  const sizedOnWaist = waistGirthCm != null && waistGirthCm > chestCircCm;
  // The girth the garment is sized against: chest, unless the waist asks more.
  const girthCm = sizedOnWaist ? waistGirthCm : chestCircCm;

  // Garment ease by fit preference (added to body circumference), from the
  // assigned flow so each product family can be cut differently.
  const easeByFit = {
    slim: num(cfg.easeSlim, 6),
    regular: num(cfg.easeRegular, 10),
    relaxed: num(cfg.easeRelaxed, 16)
  };
  const ease = easeByFit[fit] ?? easeByFit.regular;

  // The flat garment chest this customer needs: their own chest plus the
  // ease their chosen fit calls for, halved because garments are measured
  // flat. This is what the size chart is matched against.
  const estimatedChestCm = round1((girthCm + ease) / 2);

  /* Recommended size.
   *
   * Two rules, in order:
   *
   *   1. A floor. The finished garment must leave at least the slim ease over
   *      the customer's chest. Without this, nearest-match rounding hands
   *      someone a size they cannot fasten: a 41" chest is 104 cm, and the
   *      Hampden's M finishes at 108 cm, so it rounds "closest" to a coat
   *      with 4 cm of room in it, over a shirt and a knit.
   *   2. Of the sizes that clear the floor, the one finishing closest to the
   *      chest plus the ease their chosen fit calls for.
   *
   * The Hampden is graded in 5 cm steps of circumference while slim and
   * regular ease differ by 4 cm, so asking for slim often returns the same
   * size as regular. That is the honest answer: there is no half size to give
   * them, and the alternative is a coat that does not close.
   *
   * 'S:50' entries come from the flow's Size chart field: flat garment chest
   * width in cm per size, from the tech pack. Without one there is nothing
   * product-specific to match against, so the customer's own stated size is
   * better evidence than a generic chart and wins instead. */
  const sizeChart = parseChart(cfg.sizeChart);
  let recommendedSize = null;
  let sizeIsLargest = false;

  if (sizeChart) {
    const minCircCm = girthCm + easeByFit.slim;
    const targetCircCm = girthCm + ease;
    const fits = sizeChart.filter(([, width]) => width * 2 >= minCircCm);

    if (fits.length) {
      let bestDelta = Infinity;
      for (const [label, width] of fits) {
        const delta = Math.abs(width * 2 - targetCircCm);
        if (delta < bestDelta) { bestDelta = delta; recommendedSize = label; }
      }
    } else {
      /* Nothing we make is big enough. Name the largest size rather than the
       * closest one, and say so, so the screen does not quietly recommend a
       * coat that will not fasten. */
      let largest = sizeChart[0];
      for (const entry of sizeChart) {
        if (entry[1] > largest[1]) largest = entry;
      }
      recommendedSize = largest[0];
      sizeIsLargest = true;
    }
  } else {
    recommendedSize = chestAlpha || null;
    if (!recommendedSize) {
      let bestDelta = Infinity;
      for (const [label, width] of [
        ['XS', 47], ['S', 50], ['M', 53], ['L', 56], ['XL', 59], ['2XL', 62]
      ]) {
        const delta = Math.abs(width - estimatedChestCm);
        if (delta < bestDelta) { bestDelta = delta; recommendedSize = label; }
      }
    }
  }

  // Recommended build (garment length): derived from height, not asked as
  // its own question - these coats are cut in three lengths (Short/
  // Regular/Long) and height is the dominant signal for which one fits.
  // Placeholder thresholds, same caveat as the rest of this file: replace
  // with real fit-data-backed cutoffs before shipping to production.
  let recommendedBuild;
  if (!isNum(heightCm)) {
    recommendedBuild = 'Regular';
  } else if (heightCm < 170) {
    recommendedBuild = 'Short';
  } else if (heightCm <= 185) {
    recommendedBuild = 'Regular';
  } else {
    recommendedBuild = 'Long';
  }

  /* Displayed measurements.
   *
   * Only the garment chest is reported, and only as a real specified value:
   * the flow's Size chart holds the finished flat chest per size, taken from
   * the tech pack's points of measure.
   *
   * Shoulder, sleeve, collar and waist used to be shown here and have been
   * removed. They were ratio guesses off the chest (shoulder = chest * 0.84,
   * sleeve = height * 0.35 + 1) and, checked against the Hampden tech pack,
   * were out by 2-6 cm - sleeve by 6.0 cm. Collar was the customer's own
   * input handed back with no ease added. Restore them only from real
   * graded points of measure, not from a formula.
   *
   * Falls back to the ease estimate when a flow has no chart, so a product
   * without published specs still shows something rather than nothing. */
  const chartChest = lookupChart(cfg.sizeChart, recommendedSize);

  /* How much room the recommended size actually leaves over this customer's
   * chest, so the screen can say it plainly instead of implying that the fit
   * preference always moved the size. */
  const roomCm = chartChest != null ? round1(chartChest * 2 - chestCircCm) : null;

  return {
    recommendedSize,
    recommendedBuild,
    measurements: { chestCm: chartChest != null ? chartChest : estimatedChestCm },
    chestIsSpecified: chartChest != null,
    bodyChestCm: round1(chestCircCm),
    roomCm,
    easeTargetCm: ease,
    sizeIsLargest,
    /* How the chest was arrived at, for the order record: what the customer
     * stated, what height and weight suggested, and whether the waist rather
     * than the chest decided the size. */
    statedChestCm: statedChestCm != null ? round1(statedChestCm) : null,
    buildChestCm: buildChestCm != null ? round1(buildChestCm) : null,
    chestConflict,
    bodyWaistCm: bodyWaistCm != null ? round1(bodyWaistCm) : null,
    sizedOnWaist
  };

  function isNum(n) { return typeof n === 'number' && Number.isFinite(n) && n > 0; }

  function toNum(v) {
    const n = Number(v);
    return v != null && v !== '' && Number.isFinite(n) ? n : null;
  }

  function parseBand(range) {
    if (!range) return null;
    const parts = String(range).split('-').map(Number);
    if (parts.length !== 2 || !isNum(parts[0]) || !isNum(parts[1]) || parts[1] <= parts[0]) return null;
    return parts;
  }

  // Mean of a normal(mu, sd) restricted to [lo, hi].
  function truncatedMean(mu, sd, lo, hi) {
    const a = (lo - mu) / sd;
    const b = (hi - mu) / sd;
    const mass = cdf(b) - cdf(a);
    // Estimate far outside the band: the nearer edge is the answer.
    if (!(mass > 1e-9)) return mu < lo ? lo : hi;
    const mean = mu + sd * (pdf(a) - pdf(b)) / mass;
    return Math.min(hi, Math.max(lo, mean));
  }

  function pdf(x) { return Math.exp(-0.5 * x * x) / Math.sqrt(2 * Math.PI); }

  // Standard normal CDF (Abramowitz & Stegun 26.2.17, error < 7.5e-8).
  function cdf(x) {
    const t = 1 / (1 + 0.2316419 * Math.abs(x));
    const poly = t * (0.319381530 + t * (-0.356563782 + t * (1.781477937 +
      t * (-1.821255978 + t * 1.330274429))));
    const tail = pdf(x) * poly;
    return x >= 0 ? 1 - tail : tail;
  }

  function round1(n) { return Math.round(n * 2) / 2; }

  function num(v, fallback) {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  }

  function lookupChart(list, size) {
    const chart = parseChart(list);
    if (!chart || !size) return null;
    const hit = chart.find(([label]) => label === size);
    return hit ? hit[1] : null;
  }

  function parseChart(list) {
    if (!Array.isArray(list) || !list.length) return null;
    const out = [];
    for (const entry of list) {
      const [label, cm] = String(entry).split(':');
      const width = Number(cm);
      if (label && Number.isFinite(width)) out.push([label.trim(), width]);
    }
    return out.length ? out : null;
  }
};
